<script setup>
import { I18nInfo } from '@/user/models'
import clsx from 'clsx'
import { FilledButton, SpriteIcons } from '@/user/components/common'
import { CertificateInfoModal } from '@/user/dialogs'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { formatCurrency, navigateToModalTop } from '@/user/helpers/util'

defineProps({
  level: {
    type: String,
    required: true,
  },
})
const router = useRouter()
const t = new I18nInfo('certification.introduce')
const t_common = new I18nInfo('common')

const examInfo = {
  essentials: { time: 90, fee: 50000, questions: 60, descs: ['desc1', 'desc2', 'desc3'] },
  associate: { time: 120, fee: 100000, questions: 60, descs: ['desc1', 'desc2', 'desc3', 'desc4'] },
  professional: {
    time: 150,
    fee: 200000,
    questions: 60,
    descs: ['desc1', 'desc2', 'desc3', 'desc4'],
  },
}

const styles = {
  wrapper: clsx('px-16 pt-40', 'desktop:px-0'),
  certificationInfo: clsx(
    'flex flex-col gap-16 border-1 border-gray-250 rounded-12 px-20',
    'desktop:flex-row desktop:py-32 desktop:px-40 desktop:gap-0'
  ),
  certificationInfoImageCover: clsx(
    'flex items-center justify-center h-180',
    'desktop:w-376 desktop:h-250 desktop:flex-shrink-0'
  ),
  certificationInfoImage: clsx('h-140', 'desktop:h-160'),
  certificationInfoContent: clsx(
    'flex flex-col gap-12 py-20 border-t-1 border-gray-250',
    'desktop:gap-24 desktop:border-t-0 desktop:border-l-1 desktop:pl-40 desktop:py-0'
  ),
  certificationInfoTitle: clsx('text-title-2', 'desktop:text-header-5'),
  certificationInfoDescription: clsx('text-body-5 text-gray-800', 'desktop:text-body-4'),
  certificationInfoNumber: clsx('text-body-5', 'desktop:text-body-4'),
  detailButton: clsx('text-body-5 flex items-center gap-4 text-gray-600 mt-20'),
  detailButtonIcon: clsx('w-16 h-16'),
  section: clsx('px-16', 'desktop:px-0'),
  title: clsx('text-m-header-1', 'desktop:text-header-5'),
  boxInfo: clsx(
    'mt-24 mb-40 bg-gray-100 px-20 py-24 rounded-12',
    'desktop:mt-16 desktop:px-32 desktop:py-28 desktop:mb-32'
  ),
  infoItemGroup: clsx(
    'grid grid-cols-[4.8fr_5.2fr] gap-x-8 gap-y-12',
    'desktop:grid-cols-4 desktop:gap-x-40 desktop:grid-cols-[1fr_1fr_1fr_1fr]'
  ),
  infoItemColumn: clsx('grid grid-cols-[auto_1fr] gap-x-8 gap-y-12', 'desktop:contents'),
  infoItem: clsx('h-26 contents', 'desktop:flex desktop:gap-16 desktop:h-26 desktop:items-center'),
  infoItemTitle: clsx(
    'text-m-title-2 text-gray-600 whitespace-nowrap flex items-center',
    'desktop:text-body-3 desktop:w-auto'
  ),
  infoItemDesc: clsx('text-body-5 flex items-center', 'desktop:text-body-4'),
  infoItemDescEssential: clsx('text-m-title-2 text-blue-700', 'desktop:text-title-4'),
  subTitle: clsx('text-title-2', 'desktop:text-title-1'),
  noteGroup: clsx('list-disc pl-20 pt-16 text-body-5 text-gray-800', 'desktop:text-body-4'),
  CardList: clsx('flex flex-col gap-16 pt-20', 'desktop:flex-row desktop:gap-16 desktop:pt-32'),
  cardCover: clsx('', 'desktop:flex-1'),
  card: clsx(
    'group flex items-start gap-16 py-20 px-16 border-1 border-gray-250 rounded-12 hover:border-blue-700 cursor-pointer desktop:py-28 desktop:px-32 desktop:h-full'
  ),
  cardInfo: clsx('flex flex-col gap-4'),
  cardTitle: clsx('text-m-title-1 group-hover:text-blue-700', 'desktop:text-title-2'),
  desc: clsx('text-body-6 text-gray-800', 'desktop:text-body-5'),
  icon: clsx('size-24 shrink-0 text-gray-400 ml-auto group-hover:text-blue-700 self-center'),
}

const ontestManualUrl =
  process.env.VUE_APP_CLOUD_EDU_CDN_ORIGIN + '/static/exam-guide/v1/ontest-guide.pdf'

function getGuideUrl(level) {
  return process.env.VUE_APP_CLOUD_EDU_CDN_ORIGIN + `/static/exam-guide/v1/${level}.pdf`
}

function getSampleUrl(level) {
  return process.env.VUE_APP_CLOUD_EDU_CDN_ORIGIN + `/static/exam-sample/v1/${level}.html`
}

function openExamSamplePopup(level) {
  const title = ''
  const width = 665
  const height = 720

  const dualScreenLeft = window.screenLeft || screen.left
  const screenWidth = window.innerWidth || document.documentElement.clientWidth || screen.width
  const left = screenWidth / 2 - width / 2 + dualScreenLeft

  window.open(
    getSampleUrl(level),
    title,
    `scrollbars=yes, width=${width}, height=${height}, left=${left}`
  )
}

const certificateInfoModalOpen = ref(false)

function openCertificateInfoModal() {
  certificateInfoModalOpen.value = true
  setTimeout(() => {
    navigateToModalTop()
  }, 0)
}

async function navigateToExamList() {
  router.push({ name: 'CertificationExamDate' })
}
</script>
<template>
  <!-- 자격 개요 -->
  <div :class="styles.wrapper">
    <div :class="styles.certificationInfo">
      <div :class="styles.certificationInfoImageCover">
        <img
          :src="`/images/renewal/certified_${level}.png`"
          :alt="`NHN Cloud ${level}`"
          :class="styles.certificationInfoImage" />
      </div>
      <div :class="styles.certificationInfoContent">
        <strong :class="styles.certificationInfoTitle">{{
          t.translate(`section1.${level}.title`)
        }}</strong>
        <p
          :class="styles.certificationInfoDescription"
          v-html="t.translate(`section1.${level}.desc`)" />
        <p
          :class="styles.certificationInfoNumber"
          v-html="t.translate(`section1.${level}.desc2`)" />
        <button type="button" :class="styles.detailButton" @click="openCertificateInfoModal">
          {{ t_common.translate('button.detail') }}
          <SpriteIcons name="line-arrow-right" :class="styles.detailButtonIcon" />
        </button>
      </div>
    </div>
  </div>
  <CertificateInfoModal v-model="certificateInfoModalOpen" :level="level" />
  <!-- 응시 자격 및 학습 인증 항목-->
  <div :class="styles.section">
    <strong :class="styles.title">{{ t.translate(`section2.title`) }}</strong>
    <div :class="styles.boxInfo">
      <div :class="styles.infoItemGroup">
        <!-- 왼쪽 그룹 -->
        <div :class="styles.infoItemColumn">
          <dl :class="styles.infoItem">
            <dt :class="styles.infoItemTitle">{{ t.translate(`section2.level`) }}</dt>
            <dd :class="[styles.infoItemDesc, styles.infoItemDescEssential]">
              {{ t_common.translate(`certification.level.${level}`) }}
            </dd>
          </dl>
          <dl :class="styles.infoItem">
            <dt :class="styles.infoItemTitle">{{ t.translate(`section2.amount`) }}</dt>
            <dd :class="styles.infoItemDesc">
              {{ formatCurrency(examInfo[level].fee) }}{{ t.translate(`section2.currency`) }}
            </dd>
          </dl>
        </div>
        <!-- 오른쪽 그룹 -->
        <div :class="styles.infoItemColumn">
          <dl :class="styles.infoItem">
            <dt :class="styles.infoItemTitle">{{ t.translate(`section2.time`) }}</dt>
            <dd :class="styles.infoItemDesc">
              {{ examInfo[level].time }}{{ t.translate(`section2.timeUnit`) }}
            </dd>
          </dl>
          <dl :class="styles.infoItem">
            <dt :class="styles.infoItemTitle">{{ t.translate(`section2.question`) }}</dt>
            <dd :class="styles.infoItemDesc">
              {{ examInfo[level].questions }}{{ t.translate(`section2.question`) }}
            </dd>
          </dl>
        </div>
      </div>
      <FilledButton
        color="blue"
        mobile-size="medium"
        desktop-size="large"
        class="desktop:w-auto desktop:mt-32 mx-auto mt-20 w-full"
        @click="navigateToExamList()">
        {{ t.translate(`section2.apply`) }}
      </FilledButton>
    </div>
    <strong :class="styles.subTitle">{{ t.translate(`section2.subTitle`) }}</strong>
    <ul :class="styles.noteGroup">
      <li v-for="desc in examInfo[level].descs" :key="desc">
        {{ t.translate(`section2.${level}.${desc}`) }}
      </li>
    </ul>
  </div>
  <!-- 응시 전 참고사항 -->
  <div id="guide_section3" :class="styles.section">
    <strong :class="styles.title">{{ t.translate('section3.title') }}</strong>
    <ul :class="styles.noteGroup">
      <li>{{ t.translate(`section3.${level}.desc1`) }}</li>
      <li>{{ t.translate(`section3.${level}.desc2`) }}</li>
      <li>{{ t.translate('section3.common.desc1') }}</li>
      <li>
        {{ t.translate('section3.common.desc2') }}
        <a :href="ontestManualUrl" target="_blank" class="text-blue-700">
          {{ t.translate('section3.common.descLink') }}
        </a>
        {{ t.translate('section3.common.descEnd') }}
      </li>
    </ul>
    <ul :class="styles.CardList">
      <!-- 시험 준비 가이드 -->
      <li :class="styles.cardCover">
        <a :href="getGuideUrl(level)" target="_blank" :class="styles.card">
          <img src="/images/renewal/education_center_exams1.svg" alt="" />
          <div :class="styles.cardInfo">
            <strong :class="styles.cardTitle">{{ t.translate('section3.guide.link') }}</strong>
            <p :class="styles.desc">
              {{ t.translate(`levelName.${level}`) }}
              {{ t.translate('section3.guide.desc') }}
            </p>
          </div>
          <SpriteIcons name="out-link" :class="styles.icon" />
        </a>
      </li>
      <!-- 샘플 문항 -->
      <li :class="styles.cardCover">
        <a target="_blank" @click="openExamSamplePopup(level)" :class="styles.card">
          <img src="/images/renewal/education_center_exams2.svg" alt="" />
          <div :class="styles.cardInfo">
            <strong :class="styles.cardTitle">{{ t.translate('section3.sample.link') }}</strong>
            <p :class="styles.desc">
              {{ t.translate(`levelName.${level}`) }}
              {{ t.translate('section3.sample.desc') }}
            </p>
          </div>
          <SpriteIcons name="line-arrow-right" :class="styles.icon" />
        </a>
      </li>
    </ul>
  </div>
</template>
